import type { ComponentProps } from "react";
import { useCallback } from "react";
import type { TickProps } from "@ptsd/uikit";
import {
  Card,
  ChartContainer,
  ChartTooltip,
  getChartLines,
  YAxisTick,
} from "@ptsd/uikit";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, View, XStack } from "tamagui";

import { api } from "~/utils/api";
import {
  dateToReadable,
  dateToReadableByGrouBy,
} from "~/utils/date-to-readable";
import { resolveFilter, useGeneralFilter } from "~/components/blocks/filters";

interface LinearChartByScaleProps {
  title: string | null | undefined;
  scale: string;
}

type GroupBy = "week" | "month";

type CardProps = ComponentProps<typeof Card>;

export const LinearChartByScale: React.FC<
  LinearChartByScaleProps & CardProps
> = ({ title, scale, ...cardProps }) => {
  const {
    filter: { groupBy, ...rest },
  } = useGeneralFilter();

  const [data] = api.dashboard.getScaleLine.useSuspenseQuery(
    {
      filter: resolveFilter(rest),
      groupBy: (groupBy as GroupBy) ?? "week",
      scale,
    },
    {
      select: (data) => {
        return {
          config: {
            value: { label: data.name, color: "#EC1E9D" },
          },
          chartData: data.scores,
        };
      },
    },
  );

  const labelDateFormatter = useCallback(
    (value: string) => dateToReadable(value, true),
    [],
  );
  const tickFormatter = useCallback(
    (value: string) => dateToReadableByGrouBy(value, groupBy as GroupBy),
    [groupBy],
  );

  if (data.chartData.length === 0) return null;

  return (
    <Card
      data-id={"linear-chart-by-scale-card-" + scale}
      paddingVertical={"$4.1"}
      shadow="md"
      gap={"$5"}
      {...cardProps}
    >
      {title && (
        <Card.Title
          textAlign="left"
          data-id={"linear-chart-by-scale-title-" + scale}
        >
          {title}
        </Card.Title>
      )}

      <View position="relative" gap={"$4"}>
        <ChartContainer
          data-id={"linear-chart-by-scale-container-" + scale}
          height={362}
          config={data?.config}
        >
          <LineChart
            data-id={"linear-chart-by-scale-" + scale}
            accessibilityLayer
            data={data?.chartData}
            margin={{
              left: -10,
              right: 20,
              top: 12,
              bottom: 20,
            }}
          >
            <CartesianGrid
              data-id={"linear-chart-by-scale-grid-" + scale}
              strokeDasharray={"3 3"}
              stroke="#CBCBCC"
              vertical={false}
            />
            <XAxis
              data-id={"linear-chart-by-scale-x-axis-" + scale}
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={24}
              tick={{
                fill: "#636668",
                fontFamily: "Fixel Text",
                fontSize: 14,
                fontWeight: 400,
              }}
              tickFormatter={(v: string | number) =>
                tickFormatter(v.toString()) ?? ""
              }
            />
            <YAxis
              data-id={"linear-chart-by-scale-y-axis-" + scale}
              tickLine={false}
              axisLine={false}
              tickMargin={16}
              tick={(props: TickProps) => <YAxisTick {...props} />}
            />
            <Tooltip
              data-id={"linear-chart-by-scale-tooltip-" + scale}
              cursor={{
                stroke: "#CBCBCC",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              content={
                <ChartTooltip.Content
                  data-id={"linear-chart-by-scale-tooltip-content-" + scale}
                >
                  {(payload, _, date) => {
                    return (
                      <ChartTooltip.Container
                        data-id={
                          "linear-chart-by-scale-tooltip-container-" + scale
                        }
                      >
                        <ChartTooltip.Title
                          data-id={
                            "linear-chart-by-scale-tooltip-title-" + scale
                          }
                        >
                          {labelDateFormatter(date ?? "")}
                        </ChartTooltip.Title>
                        <View
                          gap={"$0.5"}
                          data-id={
                            "linear-chart-by-scale-tooltip-body-" + scale
                          }
                        >
                          {payload?.map(({ color, value, config }, i) => (
                            <XStack
                              data-id={
                                "linear-chart-by-scale-tooltip-item-" +
                                scale +
                                "-" +
                                i
                              }
                              gap={"$0.5"}
                              alignItems="center"
                              key={i}
                            >
                              <Circle
                                backgroundColor={"white"}
                                borderWidth={"$0.3"}
                                borderColor={color}
                                size={"$2"}
                              />

                              <Text
                                data-id={
                                  "linear-chart-by-scale-tooltip-item-text-" +
                                  scale
                                }
                                fontFamily={"$body"}
                                fontSize={"$1"}
                                color={"black"}
                              >
                                {config?.label} {value}
                              </Text>
                            </XStack>
                          ))}
                        </View>
                      </ChartTooltip.Container>
                    );
                  }}
                </ChartTooltip.Content>
              }
            />
            {getChartLines(data?.config).map(({ dataKey, color }, i) => (
              <Line
                data-id={"linear-chart-by-scale-line-" + scale}
                data-key={dataKey}
                key={i}
                dataKey={dataKey}
                type="linear"
                stroke={color}
                strokeWidth={2.5}
                dot={{
                  r: 8,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: "white",
                }}
                activeDot={{
                  r: 3,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: color,
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>

        {/* <Legend config={data?.config} /> */}
      </View>
    </Card>
  );
};
